import { observer } from "mobx-react-lite";
import { useMemo, useEffect } from "react";
import { MdsVirtualizedItemList } from "@/design-system/components/item-list";
import {
  MdsItemListRowData,
  MdsItemListRowType,
  MdsItemListItemRowData,
  MdsItemListSize,
} from "@/design-system/components/item-list/types";
import { MdsItemKind, MdsItem } from "@/design-system/components/item-list/types/MdsItemTypes";
import { MDSRowItemSize } from "@/mds";
import { AppStore, useAppStore } from "@/store";
import { isMac } from "@/domains/platform/isMac";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { useAsync } from "@/modules/use-async";
import { getRowForNoteObservable } from "@/store/note/getRowForNoteObservable";
import { getRowForCollectionObservable } from "@/store/collections/getRowForCollectionObservable";
import { generateShortDateString } from "@/domains/date/date";
import { DateTime } from "luxon";

export interface SuggestedSearchesListProps {}

const questionWords = ["who", "what", "when", "where", "why", "how", "which", "whose", "whom"];

export const SuggestedSearchesList = observer<SuggestedSearchesListProps>(
  function SuggestedSearchesList() {
    const { store, pageStore } = useAppStore();
    const state = pageStore.quickSearchModal;
    const currentSearchQuery = state.value;
    const onClick = state.close;
    const limit = 30;

    const { handleSearch, handleAsk, listState } = pageStore.quickSearchModal;

    const { result, invoke } = useAsync({
      fn: async () => await store.search.forSuggestions(state.value),
      options: {
        defaultValue: [],
      },
    });

    // safe to cast since we set defaultValue
    const suggestions = result as SearchSuggestion[];
    useEffect(() => {
      invoke(state.value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.value]);

    const fixedItems: MdsItem[] = useMemo(() => {
      const fixedItems: MdsItem[] = [
        {
          id: `search-${currentSearchQuery}`,
          kind: MdsItemKind.Search,
          label: `Search for "${currentSearchQuery}"`,
          onClick: ({ event }) =>
            getClickHandler(
              null,
              store,
              handleSearch,
              onClick
            )({
              event: event as
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLButtonElement>,
            }),
        },
      ];

      const lowercaseQuery = currentSearchQuery.toLowerCase();
      const isQuestion =
        lowercaseQuery.includes("?") || questionWords.some(word => lowercaseQuery.includes(word));
      if (isQuestion) {
        fixedItems.unshift({
          id: `ask-${currentSearchQuery}`,
          kind: MdsItemKind.Message,
          label: `Ask "${currentSearchQuery}"`,
          onClick: ({ event }) =>
            getClickHandler(
              null,
              store,
              handleAsk,
              onClick
            )({
              event: event as
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLButtonElement>,
            }),
        });
      }

      return fixedItems;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSearchQuery, handleAsk, onClick, store]);

    const itemRows: MdsItemListRowData[] = useMemo(() => {
      const filteredItems = suggestions
        .filter(
          item =>
            item.type === SearchSuggestionType.NOTE || item.type === SearchSuggestionType.COLLECTION
        )
        .slice(0, limit);

      // Register all item IDs in the ordered list for keyboard navigation
      const allItemIds = [
        ...fixedItems.map(item => item.id),
        ...filteredItems.map(item => item.modelId),
      ];
      const isCollection = [
        ...Array.from({ length: fixedItems.length }, () => false),
        ...filteredItems.map(item => item.type === SearchSuggestionType.COLLECTION),
      ];
      state.setOrderedItemIds(allItemIds, isCollection);

      const rows: MdsItemListRowData[] = [
        {
          key: "header",
          type: MdsItemListRowType.SectionHeader,
          payload: { title: "Suggestions" },
        },
        ...fixedItems.map(item => {
          const mdsItem: MdsItem = {
            ...item,
            listState,
          };

          const rowData: MdsItemListItemRowData = {
            type: MdsItemListRowType.Item,
            key: item.id,
            payload: mdsItem,
            size: MDSRowItemSize.Large,
          };

          return rowData;
        }),
        ...filteredItems.map(item => {
          if (item.type === SearchSuggestionType.NOTE) {
            const mdsItem: MdsItemListRowData = {
              type: MdsItemListRowType.AsyncNote,
              key: item.modelId,
              size: MdsItemListSize.Medium,
              payload: {
                noteId: item.modelId,
                itemRow: note =>
                  getRowForNoteObservable({
                    dateTime: item.lastViewedAt || "",
                    inMainPanel: true,
                    noteObservable: note,
                    listState: state.listState,
                    store,
                    disablePreview: true,
                    trackClick: onClick,
                  }),
              },
            };
            return mdsItem;
          }

          const dateLabel = generateShortDateString(
            typeof item.lastViewedAt === "string"
              ? DateTime.fromISO(item.lastViewedAt)
              : DateTime.fromJSDate(item.lastViewedAt)
          );
          const mdsItem: MdsItemListRowData = {
            type: MdsItemListRowType.AsyncCollection,
            key: item.modelId,
            size: MdsItemListSize.Medium,
            payload: {
              collectionId: item.modelId,
              itemRow: collection =>
                getRowForCollectionObservable({
                  dateLabel,
                  collectionObservable: collection,
                  listState: state.listState,
                  store,
                  disablePreview: true,
                  trackClick: onClick,
                }),
            },
          };
          return mdsItem;
        }),
      ];

      return rows;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      currentSearchQuery,
      limit,
      suggestions,
      fixedItems,
      listState,
      state,
      store,
      handleSearch,
      onClick,
    ]);

    return <MdsVirtualizedItemList rows={itemRows} />;
  }
);

// TODO: following "Recents" pattern here but we can re-do Dropdown as set of links and allow browser to take care of opening new page natively
const getClickHandler =
  (
    item: SearchSuggestion | null,
    store: AppStore,
    handleSearch: () => void,
    onClick?: () => void
  ) =>
  ({
    event,
  }: {
    event?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>;
  }) => {
    if (!item) {
      if (isMac() ? event?.metaKey : event?.ctrlKey) {
        handleSearch();
      } else {
        handleSearch();
        onClick?.();
      }

      return;
    }

    if (item.type === SearchSuggestionType.NOTE) {
      store.navigation.goToNote({ noteId: item.modelId, event });
      onClick?.();
    }
    if (item.type === SearchSuggestionType.COLLECTION) {
      store.navigation.goToCollection({ collectionId: item.modelId, event });
      onClick?.();
    }
  };
